/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : UnifiedLeftSidebar.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Divider,
  ButtonGroup,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import TextFieldsIcon from "@mui/icons-material/TextFields";
import InputIcon from "@mui/icons-material/Input";
import TableChartIcon from "@mui/icons-material/TableChart";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import SignatureIcon from "@mui/icons-material/Edit";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import FormatStrikethroughIcon from "@mui/icons-material/FormatStrikethrough";
import FormatAlignLeft from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenter from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRight from "@mui/icons-material/FormatAlignRight";
import FormatBold from "@mui/icons-material/FormatBold";
import FormatItalic from "@mui/icons-material/FormatItalic";
import FormatUnderlined from "@mui/icons-material/FormatUnderlined";
import { fontSizeDetails } from "../../../config/appTheme";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import CustomSelectV2 from "../../../components/customComponentsV2/common/select/CustomSelectV2";
import CustomFormTextFieldV2 from "../../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";

const FONT_FAMILIES = [
  {
    label: "Inter (system)",
    value:
      "Inter, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial",
  },
  { label: "Roboto", value: "Roboto, Arial, sans-serif" },
  { label: "Arial", value: "Arial, Helvetica, sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Times New Roman", value: "'Times New Roman', Times, serif" },
  { label: "Courier New", value: "'Courier New', Courier, monospace" },
];
const EDIT_TYPES = { DDH: 100, SNOW: 200, BOTH: 300 };
const EMPTY_OBJECT = Object.freeze({});

const getHeadingFontSize = (level) => {
  if (level === "H1") return 32;
  if (level === "H2") return 24;
  return 18;
};

export default function UnifiedLeftSidebar({
  open,
  onClose,
  onAddElement,
  selectedElement,
  onUpdateElement,
  onUpdateProperties,
  onDeleteElement,
  pageSettings,
  onPageSettingsChange,
}) {
  if (!open) return null;

  const disabled = !selectedElement;

  const patchProps = (patch) => {
    if (!selectedElement) return;
    const currentProps = selectedElement.properties || EMPTY_OBJECT;
    onUpdateProperties({
      ...currentProps,
      ...patch,
    });
  };

  const toggleProp = (key) => {
    if (!selectedElement) return;

    const cur = !!selectedElement.properties?.[key];
    patchProps({ [key]: !cur });
  };

  const headingPreset = (level) => {
    const size = getHeadingFontSize(level);
    patchProps({ headingLevel: level, fontSize: size });
  };

  const darkField = {
    fontSize: fontSizeDetails.body1,
    "& .MuiInputLabel-root": { color: "#fff" },
    "& .MuiOutlinedInput-root": {
      color: "#fff",
      "& fieldset": { borderColor: "#777" },
      "&:hover fieldset": { borderColor: "#fff" },
      "&.Mui-focused fieldset": { borderColor: "#fff" },
    },
    "& .MuiSvgIcon-root": { color: "#fff" },
  };

  return (
    <Box
      sx={{
        height: "100vh",
        border: "3px solid #f4f5f4ff",
        background: "black",
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": {
          display: "none",
        },
        pb: "2rem",
      }}
    >
      <Box
        sx={{
          p: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #333",
          color: "#fff",
        }}
      >
        <Typography sx={leftSideBarStyles.subTitleSizes}>
          Properties Panel
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "#fff" }}>
          <MenuIcon />
        </IconButton>
      </Box>
      <Accordion
        defaultExpanded
        sx={{
          background: "transparent",
          color: "#fff",
          margin: 0,
          "&.Mui-expanded": {
            margin: 0,
          },

          "&:before": {
            display: "none",
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
        >
          <Typography sx={leftSideBarStyles.subTitleSizes}>
            Components
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={leftSideBarStyles.iconBox}
            onClick={() => onAddElement("text")}
          >
            <TextFieldsIcon />
            <Typography sx={{ fontSize: ".6rem" }}>Text Box</Typography>
          </Box>

          <Box
            sx={leftSideBarStyles.iconBox}
            onClick={() => onAddElement("input")}
          >
            <InputIcon />
            <Typography sx={{ fontSize: ".6rem", color: "black" }}>
              Input Field
            </Typography>
          </Box>

          <Box
            sx={leftSideBarStyles.iconBox}
            onClick={() => onAddElement("divider")}
          >
            <HorizontalRuleIcon />
            <Typography sx={{ fontSize: ".6rem", color: "black" }}>
              Divider
            </Typography>
          </Box>

          <Box
            sx={leftSideBarStyles.iconBox}
            onClick={() => onAddElement("table")}
          >
            <TableChartIcon />
            <Typography sx={{ fontSize: ".6rem", color: "black" }}>
              Table
            </Typography>
          </Box>

          <Box
            sx={leftSideBarStyles.iconBox}
            onClick={() => onAddElement("signature")}
          >
            <SignatureIcon />
            <Typography sx={{ fontSize: ".6rem", color: "black" }}>
              Signature
            </Typography>
          </Box>

          <Box
            sx={leftSideBarStyles.iconBox}
            onClick={() => onAddElement("date")}
          >
            <CalendarMonthIcon />
            <Typography sx={{ fontSize: ".6rem", color: "black" }}>
              Date
            </Typography>
          </Box>

          <Box
            sx={leftSideBarStyles.iconBox}
            onClick={() => onAddElement("checkbox")}
          >
            <CheckBoxIcon />
            <Typography sx={{ fontSize: ".6rem", color: "black" }}>
              Checkbox
            </Typography>
          </Box>

          <Box
            sx={leftSideBarStyles.iconBox}
            onClick={() => onAddElement("radio")}
          >
            <RadioButtonCheckedIcon />
            <Typography sx={{ fontSize: ".6rem", color: "black" }}>
              Radio
            </Typography>
          </Box>
          <Box
            sx={[leftSideBarStyles.iconBox, { visibility: "hidden" }]}
          />
        </AccordionDetails>
      </Accordion>

      {selectedElement && (
        <Accordion
          defaultExpanded
          sx={{
            background: "transparent",
            color: "#fff",
            margin: 0,
            "&.Mui-expanded": {
              margin: 0,
            },

            "&:before": {
              display: "none",
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
          >
            <Typography sx={leftSideBarStyles.subTitleSizes}>
              Properties
            </Typography>
          </AccordionSummary>

          <AccordionDetails
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <Box
              sx={{
                width: "100%",
              }}
            >
              <CustomSelectV2
                size="small"
                label="Edit Type"
                selectedValue={
                  Number.parseInt(selectedElement?.edit_type, 10) || ""
                }
                handleOnChange={(e) =>
                  onUpdateElement("edit_type", Number(e.target.value))
                }
                optionsList={[
                  { value: EDIT_TYPES?.DDH, label: "DDH" },
                  { value: EDIT_TYPES?.SNOW, label: "SNOW" },
                  { value: EDIT_TYPES?.BOTH, label: "BOTH" },
                ]}
                txtFldStyle={{ width: "100%" }}
              />
            </Box>

            {(selectedElement.type === "text" ||
              selectedElement.type === "heading" ||
              selectedElement.type === "input" ||
              selectedElement.type === "signature") && (
              <>
                {(selectedElement.type === "text" ||
                  selectedElement.type === "heading") && (
                  <CustomFormTextFieldV2
                    required={false}
                    size="small"
                    label="Text"
                    value={selectedElement.properties?.text ?? ""}
                    setValue={(val) =>
                      patchProps({
                        ...selectedElement.properties,
                        text: val,
                      })
                    }
                  />
                )}

                <CustomSelectV2
                  label="Font Size"
                  size="small"
                  selectedValue={selectedElement.properties?.fontSize ?? 14}
                  optionsList={[
                    { value: 10, label: "10" },
                    { value: 12, label: "12" },
                    { value: 14, label: "14" },
                    { value: 16, label: "16" },
                    { value: 18, label: "18" },
                    { value: 20, label: "20" },
                    { value: 24, label: "24" },
                    { value: 32, label: "32" },
                    { value: 48, label: "48" },
                  ]}
                  handleOnChange={(e) =>
                    patchProps({ fontSize: Number(e.target.value) })
                  }
                />

                <Typography
                  sx={[leftSideBarStyles.subTitleSizes, { textAlign: "start" }]}
                >
                  Alignment:
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    mb: 1,
                    border: "1px solid #2a2a2a",
                    flex: 1,
                    width: "fit-content",
                  }}
                >
                  {[
                    { key: "left", icon: <FormatAlignLeft /> },
                    { key: "center", icon: <FormatAlignCenter /> },
                    { key: "right", icon: <FormatAlignRight /> },
                  ].map(({ key, icon }) => {
                    const isActive =
                      selectedElement.properties?.alignment === key;

                    return (
                      <IconButton
                        key={key}
                        disabled={disabled}
                        onClick={() => patchProps({ alignment: key })}
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: 0,
                          backgroundColor: isActive ? "#ffffff" : "#0b0b0b",
                          color: isActive ? "#000000" : "#ffffff",

                          "&:hover": {
                            backgroundColor: isActive ? "#ffffff" : "#1a1a1a",
                          },

                          "&.Mui-disabled": {
                            backgroundColor: "#0b0b0b",
                            color: "#555",
                          },

                          "& svg": {
                            fontSize: 18,
                          },
                        }}
                      >
                        {icon}
                      </IconButton>
                    );
                  })}
                </Box>

                {selectedElement.type === "text" ||
                selectedElement.type === "heading" ? (
                  <>
                    <Typography
                      sx={[
                        leftSideBarStyles.subTitleSizes,
                        { textAlign: "start" },
                      ]}
                    >
                      Heading Properties:
                    </Typography>
                    <ButtonGroup
                      size="small"
                      sx={{
                        mb: 1,
                        backgroundColor: "#0b0b0b",
                        border: "1px solid #2a2a2a",
                        borderRadius: 0,
                        display: "inline-flex",
                        flexWrap: "wrap",
                        width: "fit-content",
                        "& .MuiButton-root": {
                          minWidth: 44,
                          height: 36,
                          padding: "0 12px",
                          borderRadius: 0,
                          borderRight: "1px solid #2a2a2a",
                          textTransform: "none",
                          fontWeight: 500,
                          color: "#ffffff",
                          backgroundColor: "#0b0b0b",
                        },

                        "& .MuiButton-outlined": {
                          border: "none",
                          color: "#ffffff",
                        },

                        "& .MuiButton-outlined:hover": {
                          backgroundColor: "#1a1a1a",
                          border: "none",
                        },

                        "& .MuiButton-contained": {
                          backgroundColor: "#ffffff",
                          color: "#000000",
                          boxShadow: "none",
                        },

                        "& .MuiButton-contained:hover": {
                          backgroundColor: "#ffffff",
                        },

                        "& .MuiButton-root:focus-visible": {
                          outline: "none",
                          boxShadow: "inset 0 0 0 2px #26890d",
                        },

                        "& .MuiButtonGroup-grouped:last-of-type": {
                          borderRight: "none",
                        },
                      }}
                    >
                      {["H1", "H2", "H3"].map((level) => (
                        <Button
                          key={level}
                          variant={
                            selectedElement.properties?.headingLevel === level
                              ? "contained"
                              : "outlined"
                          }
                          onClick={() => headingPreset(level)}
                        >
                          {level}
                        </Button>
                      ))}

                      <Button
                        variant={
                          selectedElement.properties?.headingLevel
                            ? "outlined"
                            : "contained"
                        }
                        onClick={() =>
                          patchProps({ headingLevel: null, fontSize: 16 })
                        }
                      >
                        Normal
                      </Button>
                    </ButtonGroup>
                  </>
                ) : null}

                <Typography
                  sx={[leftSideBarStyles.subTitleSizes, { textAlign: "start" }]}
                >
                  Font Properties:
                </Typography>
                <Box
                  sx={{
                    display: "inline-flex",
                    mb: 1,
                    backgroundColor: "#0b0b0b",
                    border: "1px solid #2a2a2a",
                  }}
                >
                  {[
                    { key: "bold", icon: <FormatBold />, label: "Bold" },
                    { key: "italic", icon: <FormatItalic />, label: "Italic" },
                    {
                      key: "underline",
                      icon: <FormatUnderlined />,
                      label: "Underline",
                    },
                    {
                      key: "strike",
                      icon: <FormatStrikethroughIcon />,
                      label: "Strikethrough",
                    },
                  ].map(({ key, icon, label }) => {
                    const isActive = !!selectedElement.properties?.[key];

                    return (
                      <IconButton
                        key={key}
                        disabled={disabled}
                        aria-label={label}
                        onClick={() => toggleProp(key)}
                        sx={{
                          width: 38,
                          flex: 1,
                          height: 38,
                          borderRadius: 0,
                          backgroundColor: isActive ? "#ffffff" : "#0b0b0b",
                          color: isActive ? "#000000" : "#ffffff",

                          "&:hover": {
                            backgroundColor: isActive ? "#ffffff" : "#1a1a1a",
                          },

                          "&.Mui-disabled": {
                            backgroundColor: "#0b0b0b",
                            color: "#555",
                          },

                          "& svg": {
                            fontSize: 18,
                          },

                          "&:last-of-type": {
                            borderRight: "none",
                          },
                        }}
                      >
                        {icon}
                      </IconButton>
                    );
                  })}
                </Box>

                <CustomSelectV2
                  label="Font Family"
                  size="small"
                  selectedValue={
                    selectedElement.properties?.fontFamily ??
                    FONT_FAMILIES[0].value
                  }
                  optionsList={FONT_FAMILIES.map((f) => ({
                    value: f.value,
                    label: f.label,
                    fontFamily: f.value,
                  }))}
                  handleOnChange={(e) =>
                    patchProps({ fontFamily: e.target.value })
                  }
                />

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1,
                  }}
                >
                  <CustomFormTextFieldV2
                    label="Line Height"
                    type="number"
                    value={selectedElement.properties?.lineHeight ?? 1.3}
                    setValue={(val) => patchProps({ lineHeight: Number(val) })}
                    inputProps={{ step: 0.1 }}
                  />

                  <CustomFormTextFieldV2
                    label="Letter Spacing"
                    type="number"
                    value={selectedElement.properties?.letterSpacing ?? 0}
                    setValue={(val) =>
                      patchProps({ letterSpacing: Number(val) })
                    }
                    inputProps={{ step: 0.1 }}
                  />
                </Box>
              </>
            )}

            {selectedElement.type === "image" && (
              <>
                <Button variant="outlined" component="label" fullWidth>
                  <span>Upload Image</span>
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file || !selectedElement) return;
                      const reader = new FileReader();
                      reader.onload = () => {
                        const currentProps =
                          selectedElement.properties || EMPTY_OBJECT;
                        onUpdateProperties({
                          ...currentProps,
                          src: reader.result,
                        });
                      };
                      reader.readAsDataURL(file);
                    }}
                  />
                </Button>

                <TextField
                  label="Border Radius"
                  type="number"
                  size="small"
                  fullWidth
                  value={selectedElement.properties?.borderRadius ?? 0}
                  onChange={(e) =>
                    patchProps({ borderRadius: Number(e.target.value) })
                  }
                />
              </>
            )}

            {selectedElement.type === "table" && (
              <>
                <Typography
                  sx={[leftSideBarStyles.subTitleSizes, { textAlign: "start" }]}
                >
                  Table Columns
                </Typography>

                {(selectedElement.properties?.columns ?? []).map(
                  (col, index) => (
                    <Box
                      key={col.key || `col_${index}`}
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <CustomFormTextFieldV2
                        size="small"
                        label={`Column ${index + 1}`}
                        value={col.label}
                        setValue={(val) => {
                          const cols = [
                            ...(selectedElement.properties?.columns ?? []),
                          ];
                          cols[index] = {
                            ...cols[index],
                            label: val,
                          };
                          patchProps({ columns: cols });
                        }}
                        txtFldStyle={{
                          flex: 1,
                        }}
                      />

                      <Box
                        onMouseDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <CustomSelectV2
                          label="Type"
                          size="small"
                          sltFldStyle={{
                            minHeight: "45px",
                            fontSize: "0.95rem",
                            borderRadius: 0,
                            width: "6rem",
                          }}
                          selectedValue={col.type ?? "default"}
                          optionsList={[
                            { label: "User Input", value: "default" },
                            { label: "Fixed Value", value: "fixed" },
                          ]}
                          handleOnChange={(e) => {
                            const value =
                              typeof e === "string" ? e : e?.target?.value;

                            if (!value) return;

                            const cols = [
                              ...(selectedElement.properties?.columns ?? []),
                            ];
                            cols[index] = { ...cols[index], type: value };

                            patchProps({ columns: cols });
                          }}
                        />
                      </Box>

                      <IconButton
                        aria-label="Remove column"
                        size="small"
                        onClick={() => {
                          const cols = (
                            selectedElement.properties?.columns ?? []
                          ).filter((_, i) => i !== index);

                          patchProps({ columns: cols });
                        }}
                        sx={{
                          width: 16,
                          height: 16,
                          marginTop: "1rem",
                          padding: 0,
                          borderRadius: 1,
                          color: "#fff",

                          "&.Mui-disabled": {
                            color: "#fca5a5",
                            backgroundColor: "#fef2f2",
                          },
                        }}
                      >
                        ✕
                      </IconButton>
                    </Box>
                  ),
                )}
                <Box sx={{ display: "flex", justifyContent: "end" }}>
                  <CustomButtonV2
                    type="submit"
                    size="small"
                    btnName={"Add Column"}
                    handleOnClick={() =>
                      patchProps({
                        columns: [
                          ...(selectedElement.properties?.columns ?? []),
                          { key: `col_${Date.now()}`, label: "New Column" },
                        ],
                      })
                    }
                  />
                </Box>
                <Divider sx={{ my: 1, background: "#fff" }} />

                <CustomFormTextFieldV2
                  label="Rows"
                  type="number"
                  value={selectedElement.properties?.rows ?? 1}
                  setValue={(val) => patchProps({ rows: Number(val) })}
                  txtFldStyle={{
                    width: "100%",
                  }}
                />
              </>
            )}

            {selectedElement.type === "input" && (
              <>
                <CustomFormTextFieldV2
                  label="Placeholder"
                  value={selectedElement.properties?.placeholder ?? ""}
                  setValue={(val) => patchProps({ placeholder: val })}
                  txtFldStyle={{
                    width: "100%",
                  }}
                />

                <CustomSelectV2
                  label="Input Type"
                  size="small"
                  selectedValue={
                    selectedElement.properties?.inputType || "text"
                  }
                  optionsList={[
                    { value: "text", label: "Text" },
                    { value: "number", label: "Number" },
                  ]}
                  handleOnChange={(e) =>
                    patchProps({ inputType: e.target.value })
                  }
                />
              </>
            )}

            <Divider sx={{ background: "#fff" }} />
            {(selectedElement.type === "radio" ||
              selectedElement.type === "checkbox") && (
              <>
                <CustomFormTextFieldV2
                  label="Label"
                  value={selectedElement.properties?.label ?? ""}
                  setValue={(val) => patchProps({ label: val })}
                  txtFldStyle={{
                    width: "100%",
                  }}
                />

                <Typography
                  sx={[
                    fontSizeDetails.subtitle2,
                    { textAlign: "start", mt: 1 },
                  ]}
                >
                  Options
                </Typography>

                {(selectedElement.properties?.options ?? []).map(
                  (opt, index) => (
                    <Box
                      key={opt.key || `opt_${index}`}
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "center",
                      }}
                    >
                      <CustomFormTextFieldV2
                        size="small"
                        label={`Option ${index + 1}`}
                        value={opt.label}
                        setValue={(val) => {
                          const updated = [
                            ...(selectedElement.properties?.options ?? []),
                          ];
                          updated[index] = {
                            ...updated[index],
                            label: val,
                          };
                          patchProps({ options: updated });
                        }}
                        txtFldStyle={{
                          flex: 1,
                        }}
                      />

                      <IconButton
                        aria-label="Remove option"
                        size="small"
                        onClick={() => {
                          const updated = (
                            selectedElement.properties?.options ?? []
                          ).filter((_, i) => i !== index);

                          patchProps({ options: updated });
                        }}
                        sx={{
                          width: 16,
                          height: 16,
                          padding: 0,
                          borderRadius: 1,
                          marginTop: "1rem",
                          color: "#fff",

                          "&.Mui-disabled": {
                            color: "#fca5a5",
                            backgroundColor: "#fef2f2",
                          },
                        }}
                      >
                        ✕
                      </IconButton>
                    </Box>
                  ),
                )}

                <Box sx={{ display: "flex", justifyContent: "end" }}>
                  <CustomButtonV2
                    type="submit"
                    size="small"
                    btnName="Add Option"
                    handleOnClick={() =>
                      patchProps({
                        options: [
                          ...(selectedElement.properties?.options ?? []),
                          {
                            key: `opt_${Date.now()}`,
                            label: "New Option",
                          },
                        ],
                      })
                    }
                  />
                </Box>
              </>
            )}
            <Box sx={{ display: "flex", justifyContent: "end" }}>
              <CustomButtonV2
                type="error"
                size="small"
                btnName={"Delete Element"}
                handleOnClick={onDeleteElement}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
      <Accordion
        sx={{
          background: "transparent",
          color: "#fff",
          m: 0,
          "&.Mui-expanded": {
            margin: 0,
          },

          "&:before": {
            display: "none",
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
        >
          <Typography sx={leftSideBarStyles.subTitleSizes}>
            Page Settings
          </Typography>
        </AccordionSummary>

        <AccordionDetails sx={{ display: "grid", gap: 1 }}>
          <CustomSelectV2
            label="Page Size"
            required={false}
            size="small"
            selectedValue={pageSettings?.pageSize}
            optionsList={[
              { value: "A5", label: "A5" },
              { value: "A4", label: "A4" },
              { value: "A3", label: "A3" },
              { value: "Letter", label: "Letter" },
              { value: "Legal", label: "Legal" },
              { value: "Custom", label: "Custom" },
            ]}
            handleOnChange={(e) =>
              onPageSettingsChange({
                ...pageSettings,
                pageSize: e.target.value,
              })
            }
            sltFldStyle={{
              fontSize: fontSizeDetails.subtitle2.fontSize,
            }}
          />

          {pageSettings?.pageSize === "Custom" && (
            <>
              <TextField
                sx={darkField}
                label="Custom Width (px)"
                type="number"
                size="small"
                fullWidth
                value={pageSettings.customWidth ?? 900}
                onChange={(e) =>
                  onPageSettingsChange({
                    ...pageSettings,
                    customWidth: Number(e.target.value),
                  })
                }
              />

              <TextField
                label="Custom Height (px)"
                type="number"
                size="small"
                sx={darkField}
                fullWidth
                value={pageSettings.customHeight ?? 1200}
                onChange={(e) =>
                  onPageSettingsChange({
                    ...pageSettings,
                    customHeight: Number(e.target.value),
                  })
                }
              />
            </>
          )}

          <CustomSelectV2
            label="Orientation"
            required={false}
            size="small"
            selectedValue={pageSettings?.orientation}
            optionsList={[
              { value: "Portrait", label: "Portrait" },
              { value: "Landscape", label: "Landscape" },
            ]}
            handleOnChange={(e) =>
              onPageSettingsChange({
                ...pageSettings,
                orientation: e.target.value,
              })
            }
            sltFldStyle={{
              fontSize: fontSizeDetails.subtitle2.fontSize,
            }}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1,
            }}
          >
            <CustomFormTextFieldV2
              label="Top"
              type="number"
              value={pageSettings?.marginTop}
              setValue={(val) =>
                onPageSettingsChange({
                  ...pageSettings,
                  marginTop: Number(val),
                })
              }
            />

            <CustomFormTextFieldV2
              label="Right"
              type="number"
              value={pageSettings?.marginRight}
              setValue={(val) =>
                onPageSettingsChange({
                  ...pageSettings,
                  marginRight: Number(val),
                })
              }
            />

            <CustomFormTextFieldV2
              label="Bottom"
              type="number"
              value={pageSettings?.marginBottom}
              setValue={(val) =>
                onPageSettingsChange({
                  ...pageSettings,
                  marginBottom: Number(val),
                })
              }
            />

            <CustomFormTextFieldV2
              label="Left"
              type="number"
              value={pageSettings?.marginLeft}
              setValue={(val) =>
                onPageSettingsChange({
                  ...pageSettings,
                  marginLeft: Number(val),
                })
              }
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

const leftSideBarStyles = {
  iconBox: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    border: "1px solid #e5e7eb",
    height: "3.5rem",
    width: "3.5rem",
    alignItems: "center",
    boxShadow: 1,
    backgroundColor: "#fff",
    gap: 1,
    cursor: "pointer",
    color: "black",
  },
  subTitleSizes: {
    ...fontSizeDetails.subtitle2,
  },
};

UnifiedLeftSidebar.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onAddElement: PropTypes.func,
  onUpdateElement: PropTypes.func,
  onUpdateProperties: PropTypes.func,
  onDeleteElement: PropTypes.func,
  onPageSettingsChange: PropTypes.func,
  selectedElement: PropTypes.oneOfType([
    PropTypes.shape({
      type: PropTypes.string,
      edit_type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      properties: PropTypes.shape({
        text: PropTypes.string,
        fontSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        alignment: PropTypes.string,
        headingLevel: PropTypes.string,
        bold: PropTypes.bool,
        italic: PropTypes.bool,
        underline: PropTypes.bool,
        strike: PropTypes.bool,
        fontFamily: PropTypes.string,
        lineHeight: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
        letterSpacing: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
        src: PropTypes.any,
        borderRadius: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
        columns: PropTypes.oneOfType([
          PropTypes.array,
          PropTypes.arrayOf(
            PropTypes.shape({
              key: PropTypes.string,
              label: PropTypes.string,
              type: PropTypes.string,
            }),
          ),
          PropTypes.shape({
            filter: PropTypes.func,
            map: PropTypes.func,
          }),
        ]),
        rows: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        placeholder: PropTypes.string,
        inputType: PropTypes.string,
        label: PropTypes.string,
        options: PropTypes.oneOfType([
          PropTypes.array,
          PropTypes.arrayOf(
            PropTypes.shape({
              key: PropTypes.string,
              label: PropTypes.string,
            }),
          ),
          PropTypes.shape({
            filter: PropTypes.func,
            map: PropTypes.func,
          }),
        ]),
      }),
    }),
    PropTypes.object,
  ]),
  pageSettings: PropTypes.shape({
    pageSize: PropTypes.string,
    customWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    customHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    orientation: PropTypes.string,
    marginTop: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    marginRight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    marginBottom: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    marginLeft: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};
